import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import useFetch from "../../hooks/useFetch";
import { API } from "../../common/Constants";
import PreLoader from "../../core/common/preLoader";
import CopyRightInfo from "../../core/common/copyRightInfo";
import { getAPIHeaders, removeAccessToken } from "../../common/Utils";
import HeaderLogo from "../../core/common/headerLogo";
import { useAuthTenant } from "../../hooks/useAuthTenant";

const ResetPassword = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordResponce, setPasswordResponce] = useState({
    passwordResponceText: "Use 8 or more characters with a mix of letters, numbers, and symbols.",
    passwordResponceKey: "",
  });

  const {isLoading, error, callFetch } = useFetch();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { tenant } = useAuthTenant();
  const [tenantInfo, setTenantInfo] = useState<{
    institutionName: string;
    institutionLogo: string;
  } | null>(null);

  const fetchTenantInfo = async () => {
    if (!tenant) {
      setTenantInfo(null);
      return;
    }

    callFetch(
      API.TENANT_INFO.replace(':tenantId', tenant),
      'GET',
      { 'X-Tenant-ID': tenant },
      null,
      null
    )
      .then((response) => {
        if (response && response.settingGroups) {
          const brandingGroup = response.settingGroups.find(
            (group: any) => group.category === 'BRANDING'
          );

          if (brandingGroup && brandingGroup.settings) {
            const institutionNameSetting = brandingGroup.settings.find(
              (setting: any) => setting.settingKey === 'institution_name'
            );
            const institutionLogoSetting = brandingGroup.settings.find(
              (setting: any) => setting.settingKey === 'institution_logo'
            );

            setTenantInfo({
              institutionName: institutionNameSetting?.currentValue || 'Institution',
              institutionLogo: institutionLogoSetting?.currentValue || ''
            });
          } else {
            setTenantInfo(null);
          }
        } else {
          setTenantInfo(null);
        }
      })
      .catch(() => {
        setTenantInfo(null);
      });
  };

  useEffect(() => {
    fetchTenantInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);


  useEffect(() => {
    if(error){
      if(error.response?.data){
        setErrorMessage(error.response?.data)
      } else{
        setErrorMessage('Something went wrong please try again later.')
      }
    }
  }, [error]);

  const reset = () => {
      setPassword("")
      setPasswordResponce({
        passwordResponceText: "Use 8 or more characters with a mix of letters, numbers & symbols",
        passwordResponceKey: "",
      });
      setPasswordVisibility({
        password: false,
        confirmPassword: false,
      });
      setErrorMessage("")
  }

  const handleSubmit = (event:any) => {
    event.preventDefault()
    if(!password){
      setErrorMessage("Please provide password")
    } else if (password !== confirmPassword){
      setErrorMessage("Password and confirm password do not match")
    } else {
      setErrorMessage("")
      callFetch(API.PASSWORD,
          'PUT',
          getAPIHeaders(),
          null,
          {
              password: password,
          }
      ).then(() => {
        removeAccessToken();
        navigation(routes.resetPasswordSuccess);
        reset();
      }).catch((error) => { 
        setErrorMessage('Failed to reset password. Please try again.'); 
      });
    }
  }

  const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const onChangePassword = (password: string) => {
    setPassword(password);
    if (password.match(/^$|\s+/)) {
      setPasswordResponce({
        passwordResponceText: "Use 8 or more characters with a mix of letters, numbers & symbols",
        passwordResponceKey: "",
      });
    } else if (password.length === 0) {
      setPasswordResponce({
        passwordResponceText: "",
        passwordResponceKey: "",
      });
    } else if (password.length < 8) {
      setPasswordResponce({
        passwordResponceText: "Weak. Must contain at least 8 characters",
        passwordResponceKey: "0",
      });
    } else if (
      password.search(/[a-z]/) < 0 ||
      password.search(/[A-Z]/) < 0 ||
      password.search(/[0-9]/) < 0
    ) {
      setPasswordResponce({
        passwordResponceText: "Average. Must contain at least 1 upper case and number",
        passwordResponceKey: "1",
      });
    } else if (password.search(/(?=.*?[#?!@$%^&*-])/) < 0) {
      setPasswordResponce({
        passwordResponceText: "Almost. Must contain a special symbol",
        passwordResponceKey: "2",
      });
    } else {
      setPasswordResponce({
        passwordResponceText: "Awesome! You have a secure password.",
        passwordResponceKey: "3",
      });
    }
  };

  

  return (
    <div className="container-fuild">
      <PreLoader showLoader={isLoading} />
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-5">
            <div className="d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100 bg-primary-transparent">
              <div>
                <ImageWithBasePath src="assets/img/bg/authentication-bg-06.svg" alt="Img" />
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
              <div className="col-md-7 mx-auto vh-100">
                <form className="vh-100">
                  <div className="vh-100 d-flex flex-column justify-content-between p-4 pb-0">
                    <div className=" mx-auto mb-5 text-center">
                      <HeaderLogo 
                        url={tenantInfo?.institutionLogo}
                        name={tenantInfo?.institutionName}
                        size="L"
                      />
                    </div>
                    <div className="">
                      <div className="text-center mb-3">
                        <h2 className="mb-2">Reset Password</h2>
                        <p className="mb-0">
                          Your new password must be different from previous used
                          passwords.
                        </p>
                      </div>
                      <div>
                        <div className="input-block mb-3">
                          <div className="mb-3">
                            <label className="form-label">Password</label>
                            <div className="pass-group" id="passwordInput">
                              <input
                                type={passwordVisibility.password ? "text" : "password"}
                                value={password}
                                onChange={(e) => onChangePassword(e.target.value)}
                                className="form-control pass-input"
                                placeholder="Enter your password"
                              />
                              <span
                                className={`ti toggle-passwords ${passwordVisibility.password ? "ti-eye" : "ti-eye-off"
                                  }`}
                                onClick={() => togglePasswordVisibility("password")}
                                style={{ cursor: "pointer" }}
                              ></span>
                            </div>
                          </div>
                          <div
                            className={`password-strength d-flex ${passwordResponce.passwordResponceKey === "0"
                              ? "poor-active"
                              : passwordResponce.passwordResponceKey === "1"
                                ? "avg-active"
                                : passwordResponce.passwordResponceKey === "2"
                                  ? "strong-active"
                                  : passwordResponce.passwordResponceKey === "3"
                                    ? "heavy-active"
                                    : ""
                              }`}
                            id="passwordStrength"
                          >
                            <span id="poor" className="active" />
                            <span id="weak" className="active" />
                            <span id="strong" className="active" />
                            <span id="heavy" className="active" />
                          </div>
                        </div>
                        <p className="fs-12">{passwordResponce.passwordResponceText}</p>
                        <div className="mb-3">
                          <label className="form-label">Confirm Password</label>
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.confirmPassword
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Re-enter your password"
                              value={confirmPassword}
                            />
                            <span
                              className={`ti toggle-passwords ${passwordVisibility.confirmPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                                }`}
                              onClick={() =>
                                togglePasswordVisibility("confirmPassword")
                              }
                            ></span>
                          </div>
                        </div>
                        <div className="mb-3">
                          {
                          <div className="text-center" style={{height:12}}>
                            {
                              errorMessage
                              ?
                                <>
                                  <span className="link-danger">
                                  <i className="ti ti-alert-triangle" /> {errorMessage}
                                  </span>
                                </>
                              :
                                <></>
                              }
                            </div>
                          }
                        </div>
                        <div className="mb-3">
                          <button type="submit" onClick={handleSubmit} className="btn btn-primary w-100">
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 pb-4 text-center">
                      <CopyRightInfo/>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default ResetPassword;